import { TrashIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { notifyUser } from '@/store/Toast';
import { usesList } from '../../../../../constants';
import { setProjectUses } from '../../../../../store/Projects/Project/ProjectBudget';
import { formatNumber, projectBudgetTotal } from '../../../../../utils';
import Button from '../../../../UI/Button';
import { HookForm } from '../../../../UI/HookForm/Form';
import HookMaskedInput from '../../../../UI/HookForm/Form/MaskedInput';
import { CurrencyFormatDisplay } from '../../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';
import styles from '../Form.module.css';

export const ProjectUsesForm = ({
  ProjectUsesState,
  handleProgress,
}: {
  ProjectUsesState: any;
  handleProgress: (type: string) => void;
}) => {
  const dispatch = useDispatch();
  const { trigger, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'uses' });
  const projectUsesFormValues = useWatch({ name: 'uses' });
  const projectSourcesFormValues = useWatch({ name: 'sources' });
  const budgetTotal = formatNumber(projectBudgetTotal(projectUsesFormValues));

  const addNewSource = async () => {
    const projectUsesValidation = await trigger('uses');
    if (projectUsesValidation || getValues('uses').length === 0) {
      append({ name: '', amount: 0 });
    }
  };

  const backHandler = () => {
    const transformedData = getValues('uses');
    handleProgress('BACK');
    dispatch(setProjectUses(transformedData));
  };

  const onSubmit = (data: any) => {
    const transformedData = {
      ...data,
    };
    const usesTotal = projectBudgetTotal(transformedData.uses);
    const sourcesTotal = projectBudgetTotal(projectSourcesFormValues);

    if (usesTotal > sourcesTotal) {
      dispatch(
        notifyUser({
          showToast: true,
          toastMessage: `Uses total must be less or equal to total sources amount of $${formatNumber(sourcesTotal)}`,
          toastType: 'failed',
        })
      );
    } else {
      handleProgress('FORWARD');
      dispatch(setProjectUses(transformedData.uses));
    }
  };

  return (
    <HookForm onSubmit={onSubmit}>
      <div className='relative'>
        <h2 className={`mb-0 ${styles['form-title']}`}>Project Uses</h2>
        <p className='text-sm text-neutralColor-normal'>Optional</p>
        {fields.map((uses, idx) => {
          return (
            <div key={uses.id} className='flex items-end justify-between gap-4 my-4'>
              <div className='grid grid-cols-3 gap-2 w-full'>
                <HookSelect
                  customClassName='col-span-1'
                  label='Line Item'
                  name={`uses.${idx}.name` as const}
                  id='name'
                  options={usesList.map((d) => ({
                    label: d,
                    value: d,
                  }))}
                  required={false}
                />
                <HookMaskedInput
                  customClassName='col-span-1'
                  label='Amount ($)'
                  name={`uses.${idx}.amount` as const}
                  id='amount'
                  maskedComponent='currency'
                />
                <HookSelect
                  customClassName='col-span-1'
                  label='Paid From'
                  name={`uses.${idx}.source` as const}
                  id='name'
                  options={ProjectUsesState?.sources.map((d: any) => ({
                    label: d.name,
                    value: d.name,
                  }))}
                  required={false}
                />
              </div>
              <div
                className='h-full border p-2 my-1 rounded-md text-neutralColor-normal bg-gray-50 hover:bg-tertiaryColor-lightest hover:text-tertiaryColor-normal cursor-pointer transition duration-100 ease-in-out'
                onClick={() => remove(idx)}
              >
                <TrashIcon className='h-5 w-6' />
              </div>
            </div>
          );
        })}
        <p className='mt-5 font-bold text-primaryColor-normal'>
          Total: <CurrencyFormatDisplay value={budgetTotal} prefix='$ ' />
        </p>
        <div className='mt-8'>
          <Button label='Add Use' btnStyle='btn-primary' btnSize='btn-sm' type='button' onClick={addNewSource} />
        </div>
      </div>
      <div className='w-full flex justify-end gap-4'>
        <Button type='button' btnStyle='btn-neutral' label='Previous' onClick={backHandler} />
        <Button type='submit' btnStyle='btn-primary' label='Next' />
      </div>
    </HookForm>
  );
};
